// WP-3.2 (M2 QT-1..6, S-21/22): quotation queries/mutations.
import {
  quotationListResponseSchema,
  quotationResponseSchema,
  type QuotationConvertRequest,
  type QuotationCreateRequest,
  type QuotationEditRequest,
  type QuotationPricing,
  type QuotationResponse,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';

export const QUOTATIONS_KEY = ['quotations'] as const;

export function useQuotations(status?: string): UseQueryResult<QuotationResponse[]> {
  const qs = status !== undefined ? `?status=${status}` : '';
  return useQuery({
    queryKey: [...QUOTATIONS_KEY, status],
    queryFn: async () =>
      quotationListResponseSchema.parse(await api.get(`/api/v1/quotations${qs}`)).quotations,
  });
}

export function useQuotation(id: string): UseQueryResult<QuotationResponse> {
  return useQuery({
    queryKey: [...QUOTATIONS_KEY, id],
    queryFn: async () => quotationResponseSchema.parse(await api.get(`/api/v1/quotations/${id}`)),
    enabled: id !== '',
  });
}

function useInvalidateQuotations(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: QUOTATIONS_KEY });
  };
}

export function useCreateQuotation(): UseMutationResult<
  QuotationResponse,
  Error,
  QuotationCreateRequest
> {
  const invalidate = useInvalidateQuotations();
  return useMutation({
    mutationFn: async (input) =>
      quotationResponseSchema.parse(await api.post('/api/v1/quotations', input)),
    onSuccess: invalidate,
  });
}

export function useEditQuotation(
  id: string,
): UseMutationResult<QuotationResponse, Error, QuotationEditRequest> {
  const invalidate = useInvalidateQuotations();
  return useMutation({
    mutationFn: async (input) =>
      quotationResponseSchema.parse(await api.patch(`/api/v1/quotations/${id}`, input)),
    onSuccess: invalidate,
  });
}

export function useRevisePricing(
  id: string,
): UseMutationResult<QuotationResponse, Error, QuotationPricing> {
  const invalidate = useInvalidateQuotations();
  return useMutation({
    mutationFn: async (pricing) =>
      quotationResponseSchema.parse(
        await api.post(`/api/v1/quotations/${id}/revise-pricing`, { pricing }),
      ),
    onSuccess: invalidate,
  });
}

function useTransition(
  action: 'send' | 'accept' | 'reject' | 'expire',
): UseMutationResult<QuotationResponse, Error, string> {
  const invalidate = useInvalidateQuotations();
  return useMutation({
    mutationFn: async (id) =>
      quotationResponseSchema.parse(await api.post(`/api/v1/quotations/${id}/${action}`)),
    onSuccess: invalidate,
  });
}

export const useSendQuotation = (): UseMutationResult<QuotationResponse, Error, string> =>
  useTransition('send');
export const useAcceptQuotation = (): UseMutationResult<QuotationResponse, Error, string> =>
  useTransition('accept');
export const useRejectQuotation = (): UseMutationResult<QuotationResponse, Error, string> =>
  useTransition('reject');
export const useExpireQuotation = (): UseMutationResult<QuotationResponse, Error, string> =>
  useTransition('expire');

export function useConvertQuotation(
  id: string,
): UseMutationResult<QuotationResponse, Error, QuotationConvertRequest> {
  const invalidate = useInvalidateQuotations();
  return useMutation({
    mutationFn: async (input) =>
      quotationResponseSchema.parse(await api.post(`/api/v1/quotations/${id}/convert`, input)),
    onSuccess: invalidate,
  });
}
